import { useQuery } from '@tanstack/react-query';
import { useSelector } from 'react-redux';

import { queryClient, queryOptions, useApiMutation } from '@/services/query';
import type { EndpointBody } from '@/services/api';
import { handleErrorResponseWithToast } from '@/lib/toasts';
import { beneficiariesApi } from '@/domain/payments/api';
import { paymentsKeys } from '@/domain/payments/queries/keys';
import { payModuleAccountIdSelector } from '@/domain/env';

const FORM_STALE = 15 * 60_000;

// ---- Reads ----
// Form + headers are keyed by the active payment accountId — a new provider/account
// gives a new key, so the form/headers refetch automatically on account change.
// Deliberately NOT gated with `enabled: !!accountId`: in TanStack v4 a disabled
// query reports isLoading:true (status 'loading') until enabled, and the modal /
// grid loaders read isLoading — so a gate would pin those loaders while permissions
// are still loading. In the normal in-app flow accountId is present by the time
// these screens mount; on a hard-refresh the query fetches once with an empty key
// and self-corrects (refetch under the real key) when accountId arrives.
export const beneficiaryFormQuery = (accountId: string) =>
  queryOptions({
    queryKey: paymentsKeys.beneficiaryForm(accountId),
    queryFn: () => beneficiariesApi.getForm(),
    staleTime: FORM_STALE,
    cacheTime: FORM_STALE,
  });
export const useBeneficiaryForm = () => {
  const accountId = useSelector(payModuleAccountIdSelector);
  return useQuery(beneficiaryFormQuery(accountId));
};

export const beneficiariesGridHeadersQuery = (accountId: string) =>
  queryOptions({
    queryKey: paymentsKeys.beneficiariesGridHeaders(accountId),
    queryFn: () => beneficiariesApi.getGridHeaders(),
    staleTime: FORM_STALE,
    cacheTime: FORM_STALE,
  });
export const useBeneficiariesGridHeaders = () => {
  const accountId = useSelector(payModuleAccountIdSelector);
  return useQuery(beneficiariesGridHeadersQuery(accountId));
};

export const beneficiaryDetailsQuery = (id: string, isExternal = false) =>
  queryOptions({
    queryKey: paymentsKeys.beneficiaryDetails(id, isExternal),
    queryFn: () =>
      beneficiariesApi.getDetails({ path: { id }, query: isExternal ? { isExternal: true } : {} }),
    enabled: !!id,
  });
export const useBeneficiaryDetails = (id: string, isExternal = false) =>
  useQuery(beneficiaryDetailsQuery(id, isExternal));

// AG-Grid server-side datasource fetch (not a hook — called from getRows()).
// cacheTime: 0 — each call has a unique queryKey (the request body includes
// pagination offset / search / filters), and the rows are never re-read from
// cache (the datasource always fetches fresh), so without it every page/search
// would leave a separate cache entry around for the default 5 min and grow the
// cache during paging.
export const fetchBeneficiariesGrid = (body: EndpointBody<typeof beneficiariesApi.gridSearch>) =>
  queryClient.fetchQuery({
    queryKey: [...paymentsKeys.all, 'beneficiariesGridSearch', body],
    queryFn: () => beneficiariesApi.gridSearch({ body }),
    staleTime: 0,
    cacheTime: 0,
  });

// ---- Mutations ----
export const useCreateBeneficiaryMutation = () =>
  useApiMutation({ mutationFn: beneficiariesApi.create, onError: handleErrorResponseWithToast });

// NB: wrap `handleErrorResponseWithToast` instead of passing it directly as `onError`.
// TanStack calls onError(error, variables, context); the 2nd arg (mutation variables)
// would land in the helper's `options` param and its `path` key would override the
// default 'data.message' lookup path — silently suppressing the toast. Each of these
// mutations sends a `path` object in its variables, so the direct form breaks;
// wrapping keeps options = {}.
export const useUpdateBeneficiaryMutation = () =>
  useApiMutation({
    mutationFn: beneficiariesApi.update,
    onError: (err) => handleErrorResponseWithToast(err),
  });

export const useUpdateVendorFieldMutation = () =>
  useApiMutation({
    mutationFn: beneficiariesApi.updateVendorField,
    onError: (err) => handleErrorResponseWithToast(err),
  });

export const useDeleteBeneficiaryMutation = () =>
  useApiMutation({
    mutationFn: beneficiariesApi.remove,
    onError: (err) => handleErrorResponseWithToast(err),
  });
